import React, { useState, useEffect, useMemo } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import {
  PanelCard, PanelFooter, Section, Slider, Choice, NumberField, Toggle, Notice, EmptyState,
  useReportDirty,
} from './controls';

/**
 * Image settings, rendered from what the camera reported.
 *
 * Nothing here is hardcoded per model: `capabilities` is the camera's own answer to
 * "what can I adjust, and within what limits", and a control appears only when its key is
 * present there. So a fixed bullet camera gets four sliders and a PTZ dome with a WDR sensor
 * gets exposure, white balance, defog and ICR tuning — from the same component.
 *
 * Only the groups the user actually touched are sent on save. Re-writing a field a camera
 * merely tolerated reading is how you get a 502 out of ONVIF.
 */

const BASIC_FIELDS = [
  { key: 'brightness', label: 'Brightness' },
  { key: 'contrast', label: 'Contrast' },
  { key: 'saturation', label: 'Saturation' },
  { key: 'sharpness', label: 'Sharpness' },
];

// ONVIF's recurring {mode, level} controls. Level only applies once the feature is switched on.
const MODE_LEVEL_FEATURES = [
  { key: 'backlight_compensation', label: 'Backlight Compensation (BLC)' },
  { key: 'wide_dynamic_range', label: 'Wide Dynamic Range (WDR)' },
  { key: 'defogging', label: 'Defogging' },
  { key: 'tone_compensation', label: 'Tone Compensation' },
  { key: 'image_stabilization', label: 'Image Stabilisation' },
];

const EXPOSURE_MANUAL_FIELDS = [
  { key: 'exposure_time', label: 'Exposure Time', hint: '(µs)' },
  { key: 'gain', label: 'Gain', hint: '(dB)' },
  { key: 'iris', label: 'Iris', hint: '(dB)' },
];

const EXPOSURE_AUTO_FIELDS = [
  { key: 'min_exposure_time', label: 'Min Exposure Time', hint: '(µs)' },
  { key: 'max_exposure_time', label: 'Max Exposure Time', hint: '(µs)' },
  { key: 'min_gain', label: 'Min Gain', hint: '(dB)' },
  { key: 'max_gain', label: 'Max Gain', hint: '(dB)' },
  { key: 'min_iris', label: 'Min Iris', hint: '(dB)' },
  { key: 'max_iris', label: 'Max Iris', hint: '(dB)' },
];

/** True when the feature's current mode means "switched off", so we can hide its level. */
function isOff(mode: any) {
  return typeof mode === 'string' && mode.toUpperCase() === 'OFF';
}

export default function ImagingPanel({ config, capabilities, onSave, onAux, ptzIlluminator, loading, onDirtyChange }: any) {
  const caps = capabilities || {};
  const [values, setValues] = useState<any>({});
  const [dirty, setDirty] = useState<string[]>([]);

  const reset = () => {
    setValues(config ? JSON.parse(JSON.stringify(config)) : {});
    setDirty([]);
  };

  useEffect(reset, [config]);
  useReportDirty(dirty.length > 0, onDirtyChange);

  /** Patch a value at `path` and remember which top-level group it belongs to. */
  const update = (path: string[], value: any) => {
    setValues((prev: any) => {
      const next: any = { ...prev };
      let node: any = next;
      for (let i = 0; i < path.length - 1; i++) {
        node[path[i]] = { ...(node[path[i]] || {}) };
        node = node[path[i]];
      }
      node[path[path.length - 1]] = value;
      return next;
    });
    setDirty((prev) => (prev.includes(path[0]) ? prev : [...prev, path[0]]));
  };

  const vendorCaps = caps.vendor || {};
  const vendorVals = values.vendor || {};
  const illuminator = vendorCaps.illuminator;

  // Noise reduction: ONVIF's own NoiseReduction element when the camera populates it (the single
  // slider in Enhancement below), plus the vendor API's richer controls here.
  //
  // Labels follow the camera's own web page rather than the field names, so an operator reading
  // both sees the same words: Dahua/CP Plus calls the temporal strength "Advanced 3D" and the
  // spatial one "Advanced 2D" (TnfLevel and SnfLevel), and its separate 2DEnable/2DLevel filter
  // it does not surface at all — we do, labelled apart so it can't be confused with Advanced 2D.
  //
  // The mode belongs to 3D NR — Off / Auto / Manual on Dahua, general / advanced on Hikvision.
  // Auto (or general) carries one strength; Manual (or advanced) splits it into the spatial and
  // temporal pair. Only the branch matching the live mode is shown, because that is the only one
  // the camera honours: writing SnfLevel while it sits in Auto is silently ignored, and writing
  // the auto level moves the spatial strength with it.
  const nrCaps: any = vendorCaps.noise_reduction;
  const nrVals: any = vendorVals.noise_reduction || {};
  const nrMode = String(nrVals.mode ?? '').toLowerCase();
  const nr3dOff = ['close', 'off', 'disable', 'disabled'].includes(nrMode);
  const nr3dManual = ['manual', 'advanced', 'expert'].includes(nrMode);
  // Separate spatial/temporal strengths, as opposed to one combined 3D level.
  const nrHasFine = !!(nrCaps?.level_3d_snf || nrCaps?.level_3d_tnf);
  const nrShowFine = nrHasFine && (nr3dManual || !nrCaps?.modes);
  // Where the fine pair exists, enabled_2d/level_2d is Dahua's extra coarse filter sitting
  // alongside "Advanced 2D" and needs to read as a different thing. Everywhere else (Hikvision's
  // advanced mode, older flat Dahua) it IS the 2D strength and gets the plain name.
  const nr2dCoarse = nrHasFine;
  const nr2dLabel = nr2dCoarse ? '2D NR filter' : '2D NR';
  // Whether the 3D side has any strength to show at all, so a mode-only firmware doesn't leave
  // an empty grid row behind.
  const nr3dLevels = !!(nrCaps?.level || (nrCaps?.level_3d && !nrShowFine)
    || (nrShowFine && (nrCaps?.level_3d_tnf || nrCaps?.level_3d_snf)));
  const updateNr = (key: string, value: any) => update(['vendor', 'noise_reduction', key], value);
  const hasAnyControl = useMemo(
    () => Object.keys(caps).some((k) => k !== 'brand' && caps[k] != null),
    [caps]
  );

  const save = () => {
    const payload: any = {};
    dirty.forEach((group) => { payload[group] = values[group]; });
    onSave(payload);
  };

  if (!config && !loading) {
    return <EmptyState>Image settings could not be read from this camera.</EmptyState>;
  }

  const changedCount = dirty.length;
  const sections = {
    picture: BASIC_FIELDS.some((f) => caps[f.key]) || vendorCaps.hue || vendorCaps.gamma || vendorCaps.style,
    exposure: !!caps.exposure || vendorCaps.anti_flicker || vendorCaps.exposure_compensation,
    enhancement: MODE_LEVEL_FEATURES.some((f) => caps[f.key]) || caps.noise_reduction?.level || nrCaps,
    whiteBalance: !!caps.white_balance,
    dayNight: caps.ir_cut_filter || caps.ir_cut_filter_auto_adjustment,
    illuminator: illuminator || ptzIlluminator,
    orientation: caps.rotate || vendorCaps.mirror || vendorCaps.flip || vendorCaps.rotate90,
    focus: !!caps.focus,
  };
  // With only a couple of groups there's nothing to scroll past, so leave them open.
  const groupCount = Object.values(sections).filter(Boolean).length;
  const collapsible = groupCount > 3;

  return (
    <PanelCard
      title="Image Settings"
      description="Only the controls this camera reported are shown."
      footer={
        <PanelFooter
          dirty={changedCount > 0} loading={loading} onReset={reset} onSave={save}
          dirtyLabel={`${changedCount} group${changedCount === 1 ? '' : 's'} changed — ${dirty.join(', ')}`}
          note={hasAnyControl ? 'Adjust a control to enable applying' : undefined}
        />
      }
    >
      {!hasAnyControl && (
        <Notice icon={InformationCircleIcon}>
          This camera did not report any adjustable image settings.
        </Notice>
      )}

      {/* Picture */}
      {sections.picture && (
        <Section title="Picture" collapsible={collapsible} hint="Brightness, contrast and colour balance.">
          {/* Paired up: a full-width track for a 0–100 value is a lot of travel per step. */}
          <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            {BASIC_FIELDS.filter((f) => caps[f.key]).map((f) => (
              <Slider
                key={f.key} label={f.label} value={values[f.key]} range={caps[f.key]}
                onChange={(v: number) => update([f.key], v)} disabled={loading}
              />
            ))}
            {vendorCaps.hue && (
              <Slider label="Hue" value={vendorVals.hue} range={vendorCaps.hue}
                onChange={(v: number) => update(['vendor', 'hue'], v)} disabled={loading} />
            )}
            {vendorCaps.gamma && (
              <Slider label="Gamma" value={vendorVals.gamma} range={vendorCaps.gamma}
                onChange={(v: number) => update(['vendor', 'gamma'], v)} disabled={loading} />
            )}
          </div>
          {vendorCaps.style && (
            <div className="sm:max-w-[50%]">
              <Choice label="Picture Style" value={vendorVals.style} options={vendorCaps.style.options}
                onChange={(v: string) => update(['vendor', 'style'], v)} disabled={loading} />
            </div>
          )}
        </Section>
      )}

      {/* Exposure */}
      {sections.exposure && (
        <Section title="Exposure" collapsible={collapsible} defaultOpen={!collapsible}
          hint={caps.exposure ? 'Shutter, gain and iris behaviour.'
            : 'Flicker suppression and brightness bias.'}>
          {/* Anti-flicker and exposure compensation come from the vendor API: these models
              report no ONVIF exposure options at all, so without them the whole section is
              empty even though the camera's own page offers all three. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caps.exposure?.modes && (
              <Choice label="Mode" value={values.exposure?.mode} options={caps.exposure.modes}
                onChange={(v: string) => update(['exposure', 'mode'], v)} disabled={loading} />
            )}
            {caps.exposure?.priorities && (
              <Choice label="Priority" value={values.exposure?.priority} options={caps.exposure.priorities}
                onChange={(v: string) => update(['exposure', 'priority'], v)} disabled={loading} />
            )}
            {vendorCaps.anti_flicker && (
              <Choice label="Anti-flicker" value={vendorVals.anti_flicker}
                options={vendorCaps.anti_flicker.options} labels={vendorCaps.anti_flicker.labels}
                onChange={(v: string) => update(['vendor', 'anti_flicker'], Number(v))}
                disabled={loading} />
            )}
          </div>
          {vendorCaps.exposure_compensation && (
            <Slider label="Exposure Compensation" value={vendorVals.exposure_compensation}
              range={vendorCaps.exposure_compensation}
              onChange={(v: number) => update(['vendor', 'exposure_compensation'], v)}
              disabled={loading} />
          )}
          {/* MANUAL uses the fixed values; AUTO uses the min/max pairs as the algorithm's bounds. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caps.exposure && (String(values.exposure?.mode).toUpperCase() === 'MANUAL'
              ? EXPOSURE_MANUAL_FIELDS : EXPOSURE_AUTO_FIELDS
            ).filter((f) => caps.exposure[f.key]).map((f) => (
              <NumberField
                key={f.key} label={f.label} hint={f.hint} value={values.exposure?.[f.key]}
                range={caps.exposure[f.key]}
                onChange={(v: number) => update(['exposure', f.key], v)} disabled={loading}
              />
            ))}
          </div>
          {caps.exposure && String(values.exposure?.mode).toUpperCase() === 'AUTO' && (
            <p className="text-[11px] text-muted-foreground">
              In auto mode these values bound the camera's own exposure algorithm rather than
              setting it — tighten the max exposure time to reduce motion blur at night.
            </p>
          )}
        </Section>
      )}

      {/* Backlight / WDR / defog / tone / stabilisation */}
      {sections.enhancement && (
        <Section title="Enhancement" collapsible={collapsible} defaultOpen={!collapsible}
          hint="Scene compensation for backlight, haze and noise.">
          {/* One row per feature — its mode on the left, its own level beside it. A single flat
              grid would be tidier still, but a feature whose level is hidden (mode Off, or no
              level at all) would let the next feature's mode slide up next to it and the reader
              would pair the wrong two controls. */}
          {MODE_LEVEL_FEATURES.filter((f) => caps[f.key]).map((f) => {
            const cap = caps[f.key];
            const val = values[f.key] || {};
            return (
              <div key={f.key} className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                {cap.modes && (
                  <Choice label={f.label} value={val.mode} options={cap.modes}
                    onChange={(v: string) => update([f.key, 'mode'], v)} disabled={loading} />
                )}
                {cap.level && !isOff(val.mode) && (
                  // Without a mode there is nothing else naming the feature, so the level
                  // carries its name rather than a bare "Level" under a standalone heading.
                  <Slider label={cap.modes ? 'Level' : f.label} value={val.level} range={cap.level}
                    onChange={(v: number) => update([f.key, 'level'], v)} disabled={loading} />
                )}
              </div>
            );
          })}
          {caps.noise_reduction?.level && (
            <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
              <Slider label="Noise Reduction (NR)" value={values.noise_reduction?.level}
                range={caps.noise_reduction.level}
                onChange={(v: number) => update(['noise_reduction', 'level'], v)} disabled={loading} />
            </div>
          )}

          {/* Vendor noise reduction. 3D NR leads, as it does on the camera's own page; the
              coarse 2D filter follows, since that one has no counterpart there. Set off by the
              same rule Section uses between groups — it is a different topic from backlight and
              haze above, and it carries enough controls to need the break. */}
          {nrCaps && (
            <div className="space-y-4 border-t border-border pt-4">
              <p className="text-[12px] font-medium text-foreground">Noise Reduction</p>

              {nrCaps.modes && (
                <div className="sm:max-w-[50%]">
                  {/* "3D NR" is Dahua's own word for this selector; on Hikvision the same field
                      is the whole feature's general/advanced mode, so it must not claim to be 3D. */}
                  <Choice label={nrHasFine ? '3D NR' : 'NR Mode'} value={nrVals.mode}
                    options={nrCaps.modes}
                    onChange={(v: string) => updateNr('mode', v)} disabled={loading} />
                </div>
              )}
              {nrCaps.enabled_3d && (
                <Toggle label="3D NR" description="Averages noise across frames."
                  checked={nrVals.enabled_3d}
                  onChange={(v: boolean) => updateNr('enabled_3d', v)} disabled={loading} />
              )}

              {!nr3dOff && nr3dLevels && (
                <>
                  <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                    {nrCaps.level && (
                      <Slider label="NR Level" value={nrVals.level} range={nrCaps.level}
                        onChange={(v: number) => updateNr('level', v)} disabled={loading} />
                    )}
                    {nrCaps.level_3d && !nrShowFine && (
                      <Slider label="3D NR Level" value={nrVals.level_3d} range={nrCaps.level_3d}
                        onChange={(v: number) => updateNr('level_3d', v)} disabled={loading} />
                    )}
                    {nrShowFine && nrCaps.level_3d_tnf && (
                      <Slider label="Advanced 3D" hint="(temporal)" value={nrVals.level_3d_tnf}
                        range={nrCaps.level_3d_tnf}
                        onChange={(v: number) => updateNr('level_3d_tnf', v)} disabled={loading} />
                    )}
                    {nrShowFine && nrCaps.level_3d_snf && (
                      <Slider label="Advanced 2D" hint="(spatial)" value={nrVals.level_3d_snf}
                        range={nrCaps.level_3d_snf}
                        onChange={(v: number) => updateNr('level_3d_snf', v)} disabled={loading} />
                    )}
                  </div>
                  {nrShowFine && (
                    <p className="text-[11px] text-muted-foreground">
                      Advanced 3D smooths noise across frames — stronger settings can smear
                      movement. Advanced 2D smooths within each frame, costing fine detail.
                    </p>
                  )}
                </>
              )}

              {/* 2D is not governed by the 3D mode on any firmware we've seen, so it lives
                  outside that gate — and its switch and strength stay together. */}
              {nrCaps.enabled_2d && (
                <Toggle label={nr2dLabel}
                  description={nr2dCoarse
                    ? "A separate coarse filter, not shown on the camera's own page."
                    : 'Smooths noise within each frame.'}
                  checked={nrVals.enabled_2d}
                  onChange={(v: boolean) => updateNr('enabled_2d', v)} disabled={loading} />
              )}
              {nrCaps.level_2d && nrVals.enabled_2d !== false && (
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                  <Slider label={nr2dCoarse ? `${nr2dLabel} level` : '2D NR Level'}
                    hint={nr2dCoarse ? `(${nrCaps.level_2d.min}–${nrCaps.level_2d.max})` : undefined}
                    value={nrVals.level_2d} range={nrCaps.level_2d}
                    onChange={(v: number) => updateNr('level_2d', v)} disabled={loading} />
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {/* White balance */}
      {sections.whiteBalance && (
        <Section title="White Balance" collapsible={collapsible} defaultOpen={!collapsible}>
          {caps.white_balance.modes && (
            <Choice label="Mode" value={values.white_balance?.mode} options={caps.white_balance.modes}
              onChange={(v: string) => update(['white_balance', 'mode'], v)} disabled={loading} />
          )}
          {String(values.white_balance?.mode).toUpperCase() === 'MANUAL' && (
            <div className="space-y-3">
              {caps.white_balance.cr_gain && (
                <Slider label="Red Gain" value={values.white_balance?.cr_gain}
                  range={caps.white_balance.cr_gain}
                  onChange={(v: number) => update(['white_balance', 'cr_gain'], v)} disabled={loading} />
              )}
              {caps.white_balance.cb_gain && (
                <Slider label="Blue Gain" value={values.white_balance?.cb_gain}
                  range={caps.white_balance.cb_gain}
                  onChange={(v: number) => update(['white_balance', 'cb_gain'], v)} disabled={loading} />
              )}
            </div>
          )}
        </Section>
      )}

      {/* Day & night — the IR-cut (ICR) filter is the one ONVIF control behind both */}
      {sections.dayNight && (
        <Section title="Day & Night" collapsible={collapsible} defaultOpen={!collapsible}
          hint="Controls the IR-cut (ICR) filter in front of the sensor.">
          {caps.ir_cut_filter?.modes && (
            <Choice label="IR Cut Filter" value={values.ir_cut_filter} options={caps.ir_cut_filter.modes}
              onChange={(v: string) => update(['ir_cut_filter'], v)} disabled={loading} />
          )}
          {caps.ir_cut_filter_auto_adjustment && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {caps.ir_cut_filter_auto_adjustment.boundary_types && (
                <Choice label="ICR Switch Trigger"
                  value={values.ir_cut_filter_auto_adjustment?.boundary_type}
                  options={caps.ir_cut_filter_auto_adjustment.boundary_types}
                  onChange={(v: string) => update(['ir_cut_filter_auto_adjustment', 'boundary_type'], v)}
                  disabled={loading} />
              )}
              {caps.ir_cut_filter_auto_adjustment.boundary_offset_supported && (
                <NumberField label="Switch Offset"
                  value={values.ir_cut_filter_auto_adjustment?.boundary_offset}
                  onChange={(v: number) => update(['ir_cut_filter_auto_adjustment', 'boundary_offset'], v)}
                  disabled={loading} />
              )}
              {caps.ir_cut_filter_auto_adjustment.response_time && (
                <NumberField label="Response Time"
                  value={values.ir_cut_filter_auto_adjustment?.response_time}
                  range={caps.ir_cut_filter_auto_adjustment.response_time}
                  onChange={(v: number) => update(['ir_cut_filter_auto_adjustment', 'response_time'], v)}
                  disabled={loading} />
              )}
            </div>
          )}
        </Section>
      )}

      {/* Illuminator — vendor API where available, otherwise the ONVIF auxiliary command */}
      {sections.illuminator && (
        <Section title="Illuminator" collapsible={collapsible} defaultOpen={!collapsible}
          hint="IR / supplement light.">
          {illuminator && (
            <div className="space-y-3">
              {illuminator.modes && (
                <Choice label="Mode" value={vendorVals.illuminator?.mode} options={illuminator.modes}
                  onChange={(v: string) => update(['vendor', 'illuminator'], { ...(vendorVals.illuminator || {}), mode: v })}
                  disabled={loading} />
              )}
              {illuminator.level && (
                <Slider label="Brightness" value={vendorVals.illuminator?.level} range={illuminator.level}
                  onChange={(v: number) => update(['vendor', 'illuminator'], { ...(vendorVals.illuminator || {}), level: v })}
                  disabled={loading} />
              )}
            </div>
          )}
          {/* Aux commands fire immediately — they are not part of the save payload, so say so. */}
          {!illuminator && ptzIlluminator && (
            <div className="space-y-2">
              <div className="flex gap-2">
                {ptzIlluminator.on_command && (
                  <button type="button" disabled={loading}
                    onClick={() => onAux?.(ptzIlluminator.on_command)}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-[12px] font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40">
                    Turn On
                  </button>
                )}
                {ptzIlluminator.off_command && (
                  <button type="button" disabled={loading}
                    onClick={() => onAux?.(ptzIlluminator.off_command)}
                    className="rounded-lg border border-border bg-card px-3 py-2 text-[12px] font-medium text-foreground hover:bg-muted transition-colors disabled:opacity-40">
                    Turn Off
                  </button>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">Applies immediately — no save needed.</p>
            </div>
          )}
        </Section>
      )}

      {/* Orientation */}
      {sections.orientation && (
        <Section title="Orientation" collapsible={collapsible} defaultOpen={!collapsible}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caps.rotate?.modes && (
              <Choice label="Rotate" value={values.rotate?.mode} options={caps.rotate.modes}
                onChange={(v: string) => update(['rotate', 'mode'], v)} disabled={loading} />
            )}
            {caps.rotate?.degrees && (
              <Choice label="Degrees" value={values.rotate?.degree} options={caps.rotate.degrees}
                onChange={(v: string) => update(['rotate', 'degree'], Number(v))} disabled={loading} />
            )}
            {vendorCaps.rotate90 && (
              <Choice label="Corridor Mode" value={vendorVals.rotate90}
                options={vendorCaps.rotate90.options} labels={vendorCaps.rotate90.labels}
                onChange={(v: string) => update(['vendor', 'rotate90'], Number(v))} disabled={loading} />
            )}
          </div>
          {/* Two switches that are read together — paired on one row like the sliders above,
              rather than stacked full-width down the section. */}
          <div className="grid gap-3 sm:grid-cols-2">
            {vendorCaps.mirror && (
              <Toggle label="Mirror" description="Flip the image horizontally." checked={vendorVals.mirror}
                onChange={(v: boolean) => update(['vendor', 'mirror'], v)} disabled={loading} />
            )}
            {vendorCaps.flip && (
              <Toggle label="Flip" description="Flip the image vertically — for ceiling mounts." checked={vendorVals.flip}
                onChange={(v: boolean) => update(['vendor', 'flip'], v)} disabled={loading} />
            )}
          </div>
        </Section>
      )}

      {/* Focus */}
      {sections.focus && (
        <Section title="Focus" collapsible={collapsible} defaultOpen={!collapsible}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {caps.focus.auto_focus_modes && (
              <Choice label="Auto Focus" value={values.focus?.auto_focus_mode}
                options={caps.focus.auto_focus_modes}
                onChange={(v: string) => update(['focus', 'auto_focus_mode'], v)} disabled={loading} />
            )}
            {caps.focus.default_speed && (
              <NumberField label="Speed" value={values.focus?.default_speed}
                range={caps.focus.default_speed}
                onChange={(v: number) => update(['focus', 'default_speed'], v)} disabled={loading} />
            )}
            {caps.focus.near_limit && (
              <NumberField label="Near Limit" value={values.focus?.near_limit}
                range={caps.focus.near_limit}
                onChange={(v: number) => update(['focus', 'near_limit'], v)} disabled={loading} />
            )}
            {caps.focus.far_limit && (
              <NumberField label="Far Limit" value={values.focus?.far_limit}
                range={caps.focus.far_limit}
                onChange={(v: number) => update(['focus', 'far_limit'], v)} disabled={loading} />
            )}
          </div>
        </Section>
      )}

      {vendorCaps.brand && (
        <p className="pt-4 text-[11px] text-muted-foreground">
          Hue, gamma, style, mirror/flip, noise reduction and illuminator use the{' '}
          {vendorCaps.brand} API — ONVIF has no standard control for them.
        </p>
      )}
    </PanelCard>
  );
}
